import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  ComponentProgressDetailView,
  ManualGradeCredit,
  OverrideQuizAnswerRequest,
} from '@/types/coaching'
import { GradingSheet } from './GradingSheet'
import { pauseRowsOf, type GradingSheetRow } from './gradingRows'
import { gradingDraftScope } from '@/hooks/coaching/useGradingDraft'

interface Props {
  component: ComponentProgressDetailView
  canEdit: boolean
  /** 기존 quiz-override mutation(알림 없는 인스턴스). 실패는 reject로 전달돼야 한다. */
  onOverride: (body: Omit<OverrideQuizAnswerRequest, 'lessonId'>) => void | Promise<void>
  onSaveSettled?: () => void
  onRequestCoaching?: (quizId: string) => void
}

/**
 * 블록퀴즈(video pause) 채점표 — pause point를 시각 순 행으로 펼치고,
 * 행 저장을 기존 `POST …/quiz-override`(scope=pause, answers 없음)에 매핑한다.
 * - 서술형 행만 partialCredit(0 | 0.5 | 1)을 명시한다. 그 외는 isCorrect만 보내 백엔드가 1.0/0.0을 정한다.
 * - answers를 보내지 않으므로 기존 행의 학생 답은 보존되고, 신규 행은 오프라인 완료(빈 제출값)로 생성된다.
 */
export function PauseQuizGradingSheet({ component, canEdit, onOverride, onSaveSettled, onRequestCoaching }: Props) {
  const { t } = useTranslation('coaching')
  const rows = useMemo(() => pauseRowsOf(component), [component])
  const label = component.label || t('component.type.video')

  const saveRow = async (row: GradingSheetRow, grade: ManualGradeCredit) => {
    if (row.pauseTimeMs == null) return
    await onOverride({
      configComponentId: component.configComponentId,
      quizId: row.key,
      scope: 'pause',
      pauseTimeMs: row.pauseTimeMs,
      isCorrect: grade === 1,
      ...(row.allowPartial ? { partialCredit: grade } : {}),
    })
  }

  return (
    <GradingSheet
      draftScope={gradingDraftScope.pause(component.componentProgressId)}
      testId="pause-quiz-grading-sheet"
      title={t('gradingMode.sheetTitleBlockQuiz', { label })}
      rows={rows}
      readOnly={!canEdit}
      allowMarkAllCorrect
      saveRow={saveRow}
      onSaveSettled={onSaveSettled}
      onRequestCoaching={onRequestCoaching}
    />
  )
}
